<script setup>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { pinia } from './pinia'; 
import { messaging } from './lib/firebase';
import { onMessage } from 'firebase/messaging';
import {
  Home,
  User,
  Utensils,
  Building2,
  Armchair,
  Settings,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  ShoppingBag,
  LogOut,
  Sparkles,
  LogIn,
  UserPlus,
  Lock,
  X,
  Instagram,
  MapPin
} from "lucide-vue-next";
import GooglePlayBadge from './components/GooglePlayBadge.vue';
import { APP_STORE_URL, PLAY_STORE_URL } from './lib/appLinks';
import AppPromoBanner from './components/AppPromoBanner.vue';
import AndroidUpdateModal from './components/AndroidUpdateModal.vue';
import CookieConsent from './components/CookieConsent.vue';
import ToastHost from './components/admin/ToastHost.vue';
import ConfirmHost from './components/ConfirmHost.vue';
import AdminMobileDock from './components/layout/AdminMobileDock.vue';
import ReportFab from './components/admin/report/ReportFab.vue';
import PaymentLogos from './components/PaymentLogos.vue';
import NativeSplash from './components/NativeSplash.vue';
import { showAdmin, isNative, isAndroid } from './utils/platform';
import { useDeliveryMode } from './composables/useDeliveryMode';
import { useRegisterSW } from 'virtual:pwa-register/vue';

const { isDeliveryNavigating } = useDeliveryMode();

// Cible de navigation d'une notification push. Les fonctions envoient `data.link`
// (chemin relatif), d'anciens messages `data.route` ou `data.deeplink` ; une URL
// absolue www.clocook.fr est ramenee a son chemin.
function pushLink(data) {
  const raw = data?.route || data?.link || data?.deeplink;
  if (!raw || typeof raw !== 'string') return null;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      return u.pathname + u.search + u.hash;
    } catch (e) {
      return null;
    }
  }
  return raw.startsWith('/') ? raw : `/${raw}`;
}

// ── Bouton retour systeme Android ──
// Ordre : un overlay ou une page qui gere ses propres etapes (BaseModal,
// feuille de compte, onboarding /welcome, commande /commande/maison) consomme
// l'evenement global `clocook:back` via preventDefault ; sinon on remonte
// l'historique ; a la racine on quitte l'app (sans ca le retour ne fait rien).
let backButtonHandle = null;
async function initAndroidBackButton() {
  try {
    const { App: CapApp } = await import('@capacitor/app');
    backButtonHandle = await CapApp.addListener('backButton', () => {
      if (showMobileMenu.value) { closeMobileMenu(); return; }
      const ev = new CustomEvent('clocook:back', { cancelable: true });
      window.dispatchEvent(ev);
      if (ev.defaultPrevented) return;
      if (route.path !== '/' && route.path !== '/welcome') {
        // Pas d'entree precedente dans l'historique de l'app (arrivee par
        // lien, notification ou rechargement) : reculer sortirait de l'app,
        // on ramene a l'accueil.
        if (!window.history.state?.back) { router.replace('/'); return; }
        router.back();
        return;
      }
      CapApp.exitApp();
    });
  } catch (e) {
    console.warn('Android back button init error:', e);
  }
}

// ── Native app initialization (StatusBar, Keyboard, foreground push) ──
async function initNativeApp() {
  try {
    const [{ StatusBar, Style }, { Keyboard }, { PushNotifications }, { SplashScreen }] = await Promise.all([
      import('@capacitor/status-bar'),
      import('@capacitor/keyboard'),
      import('@capacitor/push-notifications'),
      import('@capacitor/splash-screen')
    ]);

    // Hide splash screen now that the app is loaded
    SplashScreen.hide().catch(() => {});

    // Disable pinch-to-zoom on native (not needed for app experience)
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
    }

    // StatusBar: dark content on light background
    StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(() => {});

    // Keyboard: accessory bar visible, disable WKWebView native scroll to prevent offset
    Keyboard.setAccessoryBarVisible({ isVisible: true }).catch(() => {});
    Keyboard.setScroll({ isDisabled: true }).catch(() => {});

    // When keyboard shows, manually scroll input into view within .native-scroll-container
    Keyboard.addListener('keyboardWillShow', (info) => {
      setTimeout(() => {
        const el = document.activeElement;
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) {
          // Find the closest scroll container
          const sc = el.closest('.native-scroll-container') || document.querySelector('.native-scroll-container');
          if (sc) {
            const elRect = el.getBoundingClientRect();
            const scRect = sc.getBoundingClientRect();
            // Scroll so the input is roughly centered in the visible area above keyboard
            const visibleHeight = sc.clientHeight;
            const targetOffset = elRect.top - scRect.top + sc.scrollTop - (visibleHeight / 3);
            sc.scrollTo({ top: Math.max(0, targetOffset), behavior: 'smooth' });
          } else {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 150);
    });

    // When keyboard hides, reset any residual body/html offset from WKWebView
    Keyboard.addListener('keyboardDidHide', () => {
      // Force reset any native scroll offset that might have leaked
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });

    // Foreground push listener → show in-app toast
    // Les messages sont data-only : titre et corps vivent dans notif.data.
    PushNotifications.addListener('pushNotificationReceived', (notif) => {
      console.log('[Native] Foreground push:', notif);
      const link = pushLink(notif?.data);
      notification.value = {
        title: notif?.title || notif?.data?.title || 'Notification',
        body: notif?.body || notif?.data?.body || '',
        type: 'info',
        action: link ? () => { notification.value = null; router.push(link); } : null
      };
      if (notifTimeout) clearTimeout(notifTimeout);
      notifTimeout = setTimeout(() => { notification.value = null; }, 5000);
    });

    // Navigation on push tap
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const link = pushLink(action.notification?.data);
      if (link) router.push(link);
    });
  } catch (e) {
    console.warn('Native init error:', e);
  }
}

const route = useRoute();
const router = useRouter();

// Use store inside setup scope, ensuring pinia is active
// Note: In Vue 3 <script setup>, this code runs inside setup()
// so app.use(pinia) from main.js *should* have run, but timing can be tricky if App is mounted synchronously.
// We explicitly pass the pinia instance to ensure it is found.

const authStore = useAuthStore(pinia);
const showSplash = ref(isNative);
const appBannerVisible = ref(false);
const updateAvailable = ref(false); // Persistent update banner

const { needRefresh, updateServiceWorker } = useRegisterSW({
  immediate: true,
  onRegisteredSW(swUrl, r) {
    r && setInterval(async () => {
       if (!(!r.installing && !r.waiting)) return;
       if (('connection' in navigator) && !navigator.onLine) return;
       const resp = await fetch(swUrl, { cache: 'no-store' });
       if (resp?.status === 200) await r.update();
    }, 2 * 60 * 1000); // Check every 2 minutes
  }
});

// ── Version check: compare build ID with deployed version.json ──
const _appBuildId = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : null;
let _versionInterval = null;

async function checkVersion() {
  if (!_appBuildId) return;
  try {
    const res = await fetch('/version.json?_=' + Date.now(), { cache: 'no-store' });
    if (!res.ok) return;
    const { v } = await res.json();
    if (v && v !== _appBuildId) updateAvailable.value = true;
  } catch { /* offline or error — ignore */ }
}

function applyUpdate() {
  updateAvailable.value = false;
  window.location.reload();
}

const isPrototype = computed(() => route.path.startsWith('/prototypes/'));
const isAdmin = computed(() => route.path.startsWith('/admin'));
const isUserAdmin = computed(() => authStore.user?.role === 'admin');
const notification = ref(null); // { title: string, body: string, type: 'info'|'success' }
let notifTimeout;

function onReportSent() {
  notification.value = {
    title: 'Rapport envoyé',
    body: "Merci, c'est bien enregistré.",
    type: 'success',
  };
  if (notifTimeout) clearTimeout(notifTimeout);
  notifTimeout = setTimeout(() => { notification.value = null; }, 4000);
}

// Native header: contextual page title
const nativePageTitle = computed(() => {
  const p = route.path;
  if (p === '/') return null;  // home → show logo
  if (p === '/commande/maison')      return 'Ma commande';
  if (p === '/commande/entreprise')   return 'Commande Entreprise';
  if (p === '/commande/paiement')     return 'Paiement';
  if (p.startsWith('/commande/suivi')) return 'Suivi de commande';
  if (p === '/commande')              return 'Commander';
  if (p === '/compte')                return 'Mon Compte';
  if (p === '/compte/commandes')      return 'Mes Commandes';
  if (p === '/compte/plats')          return 'Mes Plats reçus';
  if (p === '/compte/ateliers')       return 'Mes Ateliers';
  if (p === '/compte/profils')        return 'Profils';
  if (p === '/compte/preferences')    return 'Préférences & Santé';
  if (p === '/compte/infos')          return 'Mes Informations';
  if (p === '/compte/parrainage')     return 'Parrainage';
  if (p === '/compte/notifications')  return 'Notifications';
  if (p === '/compte/parametres')     return 'Paramètres';
  if (p === '/compte/entreprise')     return 'Mon Entreprise';
  if (p === '/auth/email')            return 'Connexion';
  if (p === '/auth/connexion')        return 'Connexion';
  if (p === '/auth/inscription')      return 'Inscription';
  if (p === '/auth/mot-de-passe-oublie') return 'Mot de passe oublié';
  if (p === '/decouvrir')             return 'Comment ça marche';
  if (p === '/concept')               return 'Offre Maison';
  if (p === '/entreprise')            return 'Offre Entreprise';
  if (p === '/livraison')             return 'Livraison';
  if (p === '/faq')                   return 'FAQ';
  if (p === '/contact')               return 'Contact';
  if (p === '/conservation')          return 'Conservation';
  if (p === '/parrainage')            return 'Parrainage';
  if (p === '/cgv')                   return 'Conditions de vente';
  if (p === '/mentions-legales')      return 'Mentions légales';
  return null;
});
const nativeShowBack = computed(() => isNative && route.path !== '/' && route.path !== '/welcome');

// UI State
const isScrolled = ref(false);
const showMobileMenu = ref(false);
const desktopNavRef = ref(null);
const mobileNavRef = ref(null);
const activePillStyleDesktop = ref({});
const activePillStyleMobile = ref({});

// Admin Logic Removed
const mainContainerClasses = computed(() => {
  if (isPrototype.value) return '';
  
  // If Admin: No padding
  if (isAdmin.value) {
    return 'w-full mx-auto relative z-0';
  }

  const base = 'flex-1 w-full mx-auto relative z-0';

  // Native app: simpler padding (header + dock)
  if (isNative) {
    // Welcome page has no header/dock
    if (route.path === '/welcome') return `${base} pt-0 pb-0`;
    // Logged in → has dock, needs bottom padding; guest → no dock
    // Header is fixed at top-0, height = 3.5rem + safe-area-inset-top
    // Main content must clear the full header height + a bit of breathing room
    const pb = authStore.isLoggedIn ? 'pb-[8rem]' : 'pb-6';
    return `${base} pt-[calc(3.5rem+env(safe-area-inset-top)+0.5rem)] ${pb}`;
  }
  
  // Web: original logic
  let mobilePt = 'pt-[3.5rem]';
  if (appBannerVisible.value) {
      mobilePt = 'pt-[7.5rem]'; 
  }

  // Determine bottom padding
  // If footer is visible on mobile (Home Page), we remove main padding and add it to footer
  const isFooterVisibleMobile = route.path === '/';
  // Routes immersives (dock caché) : pas de pb-[6rem] sinon scroll inutile
  const isImmersiveRoute = ['compte-gouts'].includes(route.name);
  const mobilePb = (isFooterVisibleMobile || isImmersiveRoute) ? '' : 'pb-[6rem]';

  return `${base} ${mobilePb} md:pb-0 ${mobilePt} md:pt-24`;
});

// « Commander » mène directement à la commande Maison (Kenny, 2026-09-21 :
// plus de menu Maison / Bureau, plus de détour par l'Entreprise, on limite les
// actions avant la page de commande). L'offre Entreprise reste accessible par
// la navigation et la page /entreprise.
const orderTarget = computed(() => '/commande/maison');
const goOrder = () => { router.push(orderTarget.value); };
const closeMobileMenu = () => { showMobileMenu.value = false; };

const handleFooterLogout = async () => {
  await authStore.logout();
  router.push('/');
};

const handleScroll = () => {
    isScrolled.value = window.scrollY > 20;
};

watch(needRefresh, (val) => {
    if (val) {
        updateServiceWorker(true);
    }
});

// Auto-reload when a new service worker takes control
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
    });
}

const measureAndApply = () => {
    // Desktop
    if (desktopNavRef.value) {
        const activeLink = desktopNavRef.value.querySelector('.nav-active');
        if (activeLink) {
            const { offsetLeft, offsetWidth } = activeLink;
            activePillStyleDesktop.value = {
                left: `${offsetLeft}px`,
                width: `${offsetWidth}px`,
                height: '100%',
                opacity: 1
            };
        } else {
            activePillStyleDesktop.value = { opacity: 0 };
        }
    }
    // Mobile
    if (mobileNavRef.value) {
        const activeLink = mobileNavRef.value.querySelector('.nav-active');
        if (activeLink) {
            const { offsetLeft, offsetWidth, offsetTop, offsetHeight } = activeLink;
            const paddingX = 4;
            activePillStyleMobile.value = {
                left: `${offsetLeft + (paddingX / 2)}px`,
                width: `${offsetWidth - paddingX}px`,
                height: `${offsetHeight}px`,
                top: `${offsetTop}px`,
                opacity: 1
            };
        } else {
            activePillStyleMobile.value = { opacity: 0 };
        }
    }
};

const updateIndicators = () => {
    // Wait for Vue DOM flush, then double rAF to ensure browser layout is settled
    nextTick(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(measureAndApply);
        });
    });
};

// Debounced version for resize/orientation events
let resizeTimer;
const debouncedUpdateIndicators = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(updateIndicators, 120);
};

watch(() => route.path, updateIndicators);

// Scroll to top on every route change (especially needed for Capacitor WebView)
watch(() => route.path, () => {
  nextTick(() => {
    if (isNative) {
      // On native, root is overflow:hidden — the .native-scroll-container is the scrollable element
      const sc = document.querySelector('.native-scroll-container');
      if (sc) sc.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  });
});

// Messaging Setup
onMounted(() => {
  authStore.checkAuth();
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('resize', debouncedUpdateIndicators);
  window.addEventListener('orientationchange', () => {
    // Orientation changes need extra time for layout to settle
    setTimeout(updateIndicators, 200);
    setTimeout(updateIndicators, 500);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      updateServiceWorker(false);
      // Clear PWA badge when user opens the app
      if ('clearAppBadge' in navigator) navigator.clearAppBadge().catch(() => {});
    }
  });
  window.addEventListener('online', () => updateServiceWorker(false));
  // Clear badge on launch
  if ('clearAppBadge' in navigator) navigator.clearAppBadge().catch(() => {});
  // Initial calculation
  setTimeout(updateIndicators, 100);

  // Version check: first after 10s, then every 5 min (web only)
  if (!isNative) {
    setTimeout(checkVersion, 10000);
    _versionInterval = setInterval(checkVersion, 5 * 60 * 1000);
  }

  // Listen for Foreground Messages
  if (!isNative && messaging) {
      onMessage(messaging, (payload) => {
        console.log('Message received. ', payload);
        // Set PWA app badge (iOS requires a numeric count)
        if ('setAppBadge' in navigator) {
          navigator.setAppBadge(1).catch(() => {});
        }
        // Show Toast
        const title = payload.data?.title || payload.notification?.title || "Notification";
        const body = payload.data?.body || payload.notification?.body || "";
        notification.value = {
          title,
          body,
          type: 'info'
        };
          
          // Auto hide after 5s
          if (notifTimeout) clearTimeout(notifTimeout);
          notifTimeout = setTimeout(() => {
              notification.value = null;
          }, 5000);
      });
  }

  // Native iOS: StatusBar, Keyboard, foreground push listener
  if (isNative) {
    initNativeApp();
    if (isAndroid) initAndroidBackButton();

    // Watch for auth state changes: redirect to /welcome when logged out
    watch(() => authStore.isLoggedIn, (loggedIn) => {
      if (!loggedIn && authStore.isAuthReady && route.path !== '/welcome' && !route.path.startsWith('/auth')) {
        router.replace('/welcome');
      }
    });
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', debouncedUpdateIndicators);
  clearTimeout(resizeTimer);
  if (_versionInterval) clearInterval(_versionInterval);
  if (backButtonHandle) backButtonHandle.remove().catch(() => {});
});
</script>

<template>
  <div class="min-h-screen font-sans flex flex-col" :class="{ 'h-screen overflow-hidden': isNative }">
    
    <!-- Native Splash Screen (animated logo + loader) -->
    <NativeSplash v-if="showSplash" @done="showSplash = false" />
    
    <!-- Si on n'est PAS sur une page proto, on affiche le Layout Global (Header / Dock) -->
    <template v-if="!isPrototype">

      <!-- ADMIN CONTENT REMOVED (Moved to AdminLayout) -->

      <!-- Mobile Header (Public) — hidden on native welcome screen -->
      <header v-if="!isAdmin && !(isNative && route.path === '/welcome')" class="fixed top-0 left-0 right-0 h-[calc(3.5rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] z-50 flex items-center md:hidden transition-colors duration-300" :class="[isNative ? 'liquid-glass-header' : 'shadow-sm bg-white', (!isNative && updateAvailable) ? '!bg-[#ffd93e]' : '']">
         <!-- Update available: replace logo with update prompt (web only) -->
         <button v-if="!isNative && updateAvailable" @click="applyUpdate" class="flex items-center gap-2 px-4 w-full justify-center">
            <Sparkles class="w-4 h-4 text-[#111]/70" />
            <span class="text-[#111] text-xs font-semibold">Nouvelle version disponible</span>
            <span class="bg-[#111] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">Mettre à jour</span>
         </button>

         <!-- Native: back button + logo always + address or title -->
         <template v-else-if="isNative">
           <button v-if="nativeShowBack" @click="router.back()" class="pl-3 pr-1 h-full flex items-center shrink-0">
             <ChevronLeft class="w-6 h-6 text-gray-700" />
           </button>
           <div class="flex-1 flex items-center gap-2 min-w-0" :class="nativeShowBack ? 'pl-0' : 'pl-4'">
             <!-- Logo always visible -->
             <RouterLink :to="isNative && !authStore.user ? { path: '/commande/maison', query: { onboarding: 1 } } : '/'" class="shrink-0">
               <img src="/assets/legacy/logo_yellow.svg" alt="Clo'Cook" class="h-7 w-auto" />
             </RouterLink>
             <!-- Spacer to push title/address to the right -->
             <div class="flex-1"></div>
             <!-- When on home + logged in: show delivery address -->
             <RouterLink v-if="!nativePageTitle && authStore.isLoggedIn && authStore.user?.address" to="/compte/infos" class="flex items-center gap-1 min-w-0 bg-gray-50 rounded-lg px-2 py-1 mr-4 border border-gray-100 active:bg-gray-100 transition-colors">
               <MapPin class="w-3 h-3 text-[#e94591] shrink-0" />
               <span class="text-xs text-gray-600 font-medium truncate">{{ authStore.user.address }}</span>
             </RouterLink>
             <!-- Sub-page title (right-aligned) -->
             <h1 v-else-if="nativePageTitle" class="text-[13px] font-bold text-gray-500 truncate pr-4">{{ nativePageTitle }}</h1>
           </div>
         </template>

         <!-- Web: logo -->
         <RouterLink v-else to="/" class="flex items-center gap-2 w-full justify-center">
            <img src="/assets/legacy/logo_yellow.svg" alt="Clo'Cook" class="h-8 w-auto" />
         </RouterLink>
      </header>

      <!-- Banniere de promotion de l'app (masquee dans l'app native) -->
      <!-- Pas sur /application : la page est deja dediee au telechargement, le bandeau
           ferait doublon et recouvre le haut du contenu. -->
      <AppPromoBanner v-if="!isNative && route.path !== '/application'" @visible="appBannerVisible = $event" />

      <!-- Alerte de mise a jour APK (Android hors store) -->
      <AndroidUpdateModal v-if="isNative" />

      <!-- File de toasts (useToast) cote client ; l'admin la monte dans AdminLayout -->
      <ToastHost v-if="!isAdmin" />
      <!-- Confirmations non bloquantes (useConfirm), a la place de confirm() -->
      <ConfirmHost />

      <!-- Global Toast Notification -->
      <div v-if="notification" class="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[90%] max-w-sm animate-slide-down cursor-pointer" @click="notification.action ? notification.action() : (notification = null)">
          <div class="bg-white/90 backdrop-blur-xl border border-gray-100 shadow-md rounded-2xl p-4 flex items-start gap-3">
              <div class="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center shrink-0 text-[#e94591]">
                  <component :is="notification.icon || ShoppingBag" class="w-5 h-5" />
              </div>
              <div class="flex-1">
                  <h3 class="font-bold text-sm text-gray-900">{{ notification.title }}</h3>
                  <p class="text-xs text-gray-500 mt-1 leading-snug">{{ notification.body }}</p>
              </div>
              <button class="text-gray-400 hover:text-gray-900" @click.stop="notification = null">
                  <X class="w-4 h-4" />
              </button>
          </div>
      </div>




      <!-- Desktop Header (Capsule) — not shown in native app -->
      <header v-if="!isAdmin && !isNative" :class="['hidden md:block fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]', isScrolled ? 'pt-2' : 'pt-4']">
        <div class="px-6 max-w-7xl mx-auto">
          <nav class="flex items-center justify-between bg-white/90 backdrop-blur-xl border border-white/60 rounded-full pl-2 pr-2 py-2 shadow-md ring-1 ring-black/5 hover:shadow-lg transition-all duration-500 ease-out">
            
            <!-- Logo -->
            <RouterLink to="/" class="flex items-center gap-2 pl-4 pr-2 group">
               <img src="/assets/legacy/logo_yellow.svg" alt="Clo'Cook" class="h-8 w-auto transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3" />
            </RouterLink>

            <!-- Menu Items -->
            <div class="flex items-center gap-1 pr-2 relative" ref="desktopNavRef">
              <!-- Active Pill Indicator (Yellow) -->
              <div 
                class="absolute bg-[#ffd93e] rounded-full transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] z-0 shadow-sm shadow-[#ffd93e]/30"
                :style="activePillStyleDesktop"
              ></div>

              <RouterLink 
                to="/" 
                class="relative px-4 py-2 rounded-full text-gray-600 font-bold text-sm hover:bg-gray-100/30 transition-colors flex items-center gap-2 group z-10"
                :class="{ 'nav-active text-gray-900': route.path === '/' }"
              >
                <Home class="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                Accueil
              </RouterLink>
              <RouterLink 
                to="/concept" 
                class="relative px-4 py-2 rounded-full text-gray-600 font-bold text-sm hover:bg-gray-100/30 transition-colors flex items-center gap-2 group z-10"
                :class="{ 'nav-active text-gray-900': route.path.startsWith('/concept') || route.path.startsWith('/commande/maison') }"
              >
                <Armchair class="h-4 w-4 group-hover:-translate-y-1 transition-transform duration-300" />
                Maison
              </RouterLink>
              <RouterLink 
                to="/entreprise" 
                class="relative px-4 py-2 rounded-full text-gray-600 font-bold text-sm hover:bg-gray-100/30 transition-colors flex items-center gap-2 group z-10"
                :class="{ 'nav-active text-gray-900': route.path.startsWith('/entreprise') || route.path.startsWith('/commande/entreprise') }"
              >
                <Building2 class="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                Entreprise
              </RouterLink>
              <template v-if="authStore.isLoggedIn">
                <RouterLink 
                  to="/compte" 
                  class="relative px-4 py-2 rounded-full text-gray-600 font-bold text-sm hover:bg-gray-100/30 transition-colors flex items-center gap-2 group z-10"
                  :class="{ 'nav-active text-gray-900': route.path.startsWith('/compte') }"
                >
                  <User class="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                  Compte
                </RouterLink>
              </template>
              <template v-else>
                 <div class="flex items-center gap-1 ml-2 mr-2 no-pill">
                    <RouterLink 
                      to="/auth/connexion" 
                      class="px-4 py-2 rounded-full text-gray-600 font-bold text-sm hover:bg-gray-100/30 transition-colors flex items-center gap-2 z-10"
                      :class="{ 'nav-active text-gray-900': route.path === '/auth/connexion' }"
                    >
                       <LogIn class="h-4 w-4" />
                       Connexion
                    </RouterLink>
                    <RouterLink 
                      to="/auth/inscription" 
                      class="px-4 py-2 rounded-full text-gray-600 font-bold text-sm hover:bg-gray-100/30 transition-colors flex items-center gap-2 z-10"
                      :class="{ 'nav-active text-gray-900': route.path === '/auth/inscription' }"
                    >
                       <UserPlus class="h-4 w-4" />
                       Inscription
                    </RouterLink>
                 </div>
              </template>
              
              <div class="h-6 w-px bg-gray-200 mx-2 no-pill"></div>

              <!-- Admin Button (if admin and admin build) -->
              <RouterLink 
                v-if="isUserAdmin && showAdmin" 
                to="/admin" 
                class="relative overflow-hidden px-6 py-2.5 rounded-full bg-[#0077B6] text-white font-bold text-sm shadow-sm shadow-[#0077B6]/30 hover:shadow-md hover:shadow-[#0077B6]/40 active:scale-95 transition-all duration-300 group flex items-center gap-2 no-pill"
              >
                <LayoutDashboard class="h-4 w-4 relative z-10" />
                <span class="relative z-10">Admin</span>
              </RouterLink>

              <!-- Commander (desktop) : accès direct à la commande -->
              <RouterLink v-else :to="orderTarget"
                          class="no-pill relative overflow-hidden px-6 py-2.5 rounded-full bg-[#ffd93e] hover:bg-[#ffcf0f] text-gray-900 font-bold text-sm shadow-sm shadow-[#ffd93e]/30 hover:shadow-md hover:shadow-[#ffd93e]/40 transition-all duration-300 flex items-center gap-2 active:scale-95">
                <Utensils class="h-4 w-4 relative z-10" />
                <span class="relative z-10">Commander</span>
                <ChevronRight class="w-3 h-3 relative z-10" />
              </RouterLink>
            </div>
          </nav>
        </div>
      </header>

      <!-- Main Content -->
      <component :is="isAdmin ? 'div' : 'main'" :class="[mainContainerClasses, isNative ? 'native-scroll-container' : '']">
        <RouterView v-slot="{ Component }">
          <KeepAlive include="AdminLivraison">
            <component :is="Component" />
          </KeepAlive>
        </RouterView>
      </component>

      <template v-if="!isAdmin">
      <!-- Footer (Desktop & Mobile Home) — hidden on native app -->
      <footer
        v-if="!isNative"
        class="mt-20 border-t border-gray-200 bg-white/50 backdrop-blur-sm"
        :class="route.path === '/' ? 'block pb-[8rem] md:pb-0' : 'hidden md:block'"
      >
        <div class="max-w-7xl mx-auto px-6 py-8 md:py-12">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8 md:gap-8">
            <div class="col-span-2 md:col-span-1">
               <img src="/assets/legacy/logo_yellow.svg" alt="Clo'Cook" class="h-6 md:h-8 w-auto mb-3 md:mb-4" />
               <p class="text-xs md:text-sm text-gray-500 leading-relaxed">
                 Cuisiné avec amour, livré avec soin.<br>
                 Simplifie ton quotidien.
               </p>
               <a href="https://www.instagram.com/clocook_fr/" target="_blank" rel="noopener" class="inline-flex items-center gap-1.5 mt-3 text-xs text-gray-500 hover:text-[#E1306C] transition-colors">
                 <Instagram class="h-4 w-4" />
                 @clocook_fr
               </a>

               <!-- Telechargement de l'app : chaque badge officiel mene a la fiche
                    de son store (regles Apple et Google). -->
               <p class="text-xs font-bold text-gray-900 mt-5 mb-2">Télécharger l'app</p>
               <div class="flex flex-wrap items-center gap-2">
                 <a :href="APP_STORE_URL" target="_blank" rel="noopener" class="inline-block hover:opacity-80 transition-opacity">
                   <img src="/badges/telecharger-dans-app-store.svg" alt="Télécharger dans l'App Store" class="h-10 w-auto" />
                 </a>
                 <a :href="PLAY_STORE_URL" target="_blank" rel="noopener" class="inline-block hover:opacity-80 transition-opacity">
                   <GooglePlayBadge />
                 </a>
               </div>
            </div>
            <div>
              <h3 class="font-bold text-gray-900 mb-3 md:mb-4 text-sm md:text-base">Découvrir</h3>
              <ul class="space-y-2 text-xs md:text-sm text-gray-500">
                <li><RouterLink to="/concept" class="hover:text-[#e94591] transition-colors">Offre Maison</RouterLink></li>
                <li><RouterLink to="/entreprise" class="hover:text-[#e94591] transition-colors">Offre Entreprise</RouterLink></li>
                <li><RouterLink to="/livraison" class="hover:text-[#e94591] transition-colors">Livraison</RouterLink></li>
                <li><RouterLink to="/conservation" class="hover:text-[#e94591] transition-colors">Conservation</RouterLink></li>
                <li><RouterLink to="/application" class="hover:text-[#e94591] transition-colors">L'application</RouterLink></li>
              </ul>
            </div>
            <div>
              <h3 class="font-bold text-gray-900 mb-3 md:mb-4 text-sm md:text-base">Aide</h3>
              <ul class="space-y-2 text-xs md:text-sm text-gray-500">
                <li><RouterLink to="/faq" class="hover:text-[#e94591] transition-colors">FAQ</RouterLink></li>
                <li><RouterLink to="/parrainage" class="hover:text-[#e94591] transition-colors">Parrainage</RouterLink></li>
                <li><RouterLink to="/contact" class="hover:text-[#e94591] transition-colors">Contact</RouterLink></li>
                <li><RouterLink to="/cgv" class="hover:text-[#e94591] transition-colors">CGV</RouterLink></li>
                <li><RouterLink to="/mentions-legales" class="hover:text-[#e94591] transition-colors">Mentions Légales</RouterLink></li>
              </ul>
            </div>
            <div>
              <h3 class="font-bold text-gray-900 mb-3 md:mb-4 text-sm md:text-base">Compte</h3>
              <ul v-if="authStore.isLoggedIn" class="space-y-2 text-xs md:text-sm text-gray-500">
                <li><RouterLink to="/compte" class="hover:text-[#e94591] transition-colors">Mon compte</RouterLink></li>
                <li>
                  <button @click="handleFooterLogout" class="hover:text-[#e94591] transition-colors text-left">
                    Déconnexion
                  </button>
                </li>
              </ul>
              <ul v-else class="space-y-2 text-xs md:text-sm text-gray-500">
                <li><RouterLink to="/auth/connexion" class="hover:text-[#e94591] transition-colors">Connexion</RouterLink></li>
                <li><RouterLink to="/auth/inscription" class="hover:text-[#e94591] transition-colors">Inscription</RouterLink></li>
              </ul>
            </div>
          </div>
          <div class="mt-8 md:mt-12 pt-8 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center text-xs text-center md:text-left text-gray-500 gap-4">
            <div class="flex flex-col md:flex-row items-center gap-2 md:gap-8">
              <p>© 2026 Clo'Cook. Tous droits réservés.</p>
              <p>Photos non contractuelles.</p>
              <p>Propulsé par <a href="https://www.thecompagnie.eu" target="_blank" rel="noopener" class="hover:text-[#e94591] transition-colors underline-offset-2 hover:underline">www.TheCompagnie.eu</a></p>
            </div>

            <PaymentLogos />
          </div>
        </div>
      </footer>

      <!-- ═══════════ NATIVE iOS DOCK — Liquid Glass ═══════════ -->
      <div v-if="isNative && authStore.isLoggedIn && !['admin-livraison'].includes(route.name) && !isDeliveryNavigating" class="fixed bottom-0 left-0 right-0 z-[70]">
        <div class="liquid-glass-dock pb-[calc(env(safe-area-inset-bottom)+4px)] pt-2 px-6">
          <div class="flex items-end justify-between h-14">
            
            <!-- Accueil -->
            <RouterLink to="/" class="flex-1 flex flex-col items-center justify-center gap-0.5 h-full transition-colors duration-200"
                        :class="route.path === '/' ? 'text-[#e94591]' : 'text-gray-500/80'">
              <Home class="h-[22px] w-[22px] transition-transform duration-200" :class="{ 'scale-110': route.path === '/' }" />
              <span class="text-xs font-semibold">Accueil</span>
            </RouterLink>

            <!-- Commandes (regroupe aussi "Mes plats reçus", onglet interne à la page) -->
            <RouterLink to="/compte/commandes" class="flex-1 flex flex-col items-center justify-center gap-0.5 h-full transition-colors duration-200"
                        :class="route.path === '/compte/commandes' || route.path.startsWith('/compte/plats') ? 'text-[#e94591]' : 'text-gray-500/80'">
              <ShoppingBag class="h-[22px] w-[22px]" />
              <span class="text-xs font-semibold">Commandes</span>
            </RouterLink>

            <!-- Commander (centre, pilule surélevée) : accès direct à la commande. -->
            <div class="relative -top-5 z-30 mx-4">
              <button @click="goOrder" aria-label="Commander"
                      class="w-14 h-14 rounded-full flex items-center justify-center active:scale-90 transition-all duration-200 border-[3px] border-white/60 ring-1 shadow-lg bg-[#ffd93e] text-gray-900 shadow-[#ffd93e]/30 ring-white/20">
                <Utensils class="h-6 w-6" />
              </button>
              <span class="absolute -bottom-4 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-500/70 whitespace-nowrap">Commander</span>
            </div>

            <!-- Profil (page /compte réduite au widget "Profils du foyer" : régimes/allergies/goûts) -->
            <RouterLink to="/compte" class="flex-1 flex flex-col items-center justify-center gap-0.5 h-full transition-colors duration-200"
                        :class="route.path === '/compte' || route.path.startsWith('/compte/profils') ? 'text-[#e94591]' : 'text-gray-500/80'">
              <User class="h-[22px] w-[22px]" />
              <span class="text-xs font-semibold">Profil</span>
            </RouterLink>

            <!-- Compte (hub réglages : infos, ateliers, parrainage, notifications, informations légales) -->
            <RouterLink to="/compte/parametres" class="flex-1 flex flex-col items-center justify-center gap-0.5 h-full transition-colors duration-200"
                        :class="route.path.startsWith('/compte') && route.path !== '/compte' && route.path !== '/compte/commandes' && !route.path.startsWith('/compte/profils') && !route.path.startsWith('/compte/plats') ? 'text-[#e94591]' : 'text-gray-500/80'">
              <Settings class="h-[22px] w-[22px]" />
              <span class="text-xs font-semibold">Compte</span>
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- ═══════════ WEB DOCK (Mobile - CLIENT) — never on native ═══════════ -->
      <div v-else-if="!isAdmin && !isNative && !['admin-livraison'].includes(route.name) && !isDeliveryNavigating" class="fixed bottom-0 left-0 right-0 z-[70] md:hidden">
        <div class="bg-white border-t border-gray-200 pb-[calc(env(safe-area-inset-bottom)+5px)] pt-2 px-4 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] relative">
          
          <div class="flex items-end justify-between relative z-10 h-14" ref="mobileNavRef">
              <!-- Sliding Pill Background -->
              <div 
                class="absolute bg-[#ffd93e] rounded-2xl transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] -z-10"
                :style="activePillStyleMobile"
              ></div>

              <!-- Left Items -->
              <RouterLink 
                to="/" 
                class="flex-1 flex flex-col items-center justify-center gap-0.5 h-full text-gray-500 hover:text-gray-900 transition-colors relative z-20"
                :class="{ 'nav-active text-gray-900': route.path === '/' }"
              >
                  <Home class="h-6 w-6 transition-transform duration-300" :class="{ '-translate-y-0.5': route.path === '/' }" />
                  <span class="text-xs font-bold">Accueil</span>
              </RouterLink>

              <RouterLink 
                to="/concept" 
                class="flex-1 flex flex-col items-center justify-center gap-0.5 h-full text-gray-500 hover:text-gray-900 transition-colors relative z-20"
                :class="{ 'nav-active text-gray-900': route.path.startsWith('/concept') || route.path.startsWith('/commande/maison') }"
              >
                  <Armchair class="h-6 w-6 transition-transform duration-300" :class="{ '-translate-y-0.5': route.path === '/concept' }" />
                  <span class="text-xs font-bold">Maison</span>
              </RouterLink>

              <!-- Center Button (Elevated) -->
              <div class="relative -top-6 z-30 mx-3">
                 
                 <!-- Admin Link -->
                 <template v-if="isUserAdmin">
                    <RouterLink 
                        to="/admin" 
                        class="w-14 h-14 rounded-full bg-[#0077B6] text-white shadow-lg shadow-[#0077B6]/40 flex items-center justify-center transform transition-transform hover:scale-105 active:scale-95 border-4 border-white"
                    >
                        <LayoutDashboard class="h-6 w-6" />
                    </RouterLink>
                    <span class="absolute -bottom-4 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-900 whitespace-nowrap">Admin</span>
                 </template>

                 <!-- Commander : accès direct à la commande -->
                 <template v-else>
                    <button @click="goOrder" aria-label="Commander"
                            class="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transform transition-transform hover:scale-105 active:scale-95 border-4 border-white bg-[#ffd93e] text-gray-900 shadow-[#ffd93e]/40">
                        <Utensils class="h-6 w-6" />
                    </button>
                    <span class="absolute -bottom-4 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-900 whitespace-nowrap">Commander</span>
                 </template>
              </div>

              <!-- Right Items -->
              <RouterLink 
                to="/entreprise" 
                class="flex-1 flex flex-col items-center justify-center gap-0.5 h-full text-gray-500 hover:text-gray-900 transition-colors relative z-20"
                :class="{ 'nav-active text-gray-900': route.path.startsWith('/entreprise') || route.path.startsWith('/commande/entreprise') }"
              >
                  <Building2 class="h-6 w-6 transition-transform duration-300" :class="{ '-translate-y-0.5': route.path === '/entreprise' }" />
                  <span class="text-xs font-bold">Entreprise</span>
              </RouterLink>
              
              <template v-if="authStore.isLoggedIn">
                <RouterLink 
                  to="/compte" 
                  class="flex-1 flex flex-col items-center justify-center gap-0.5 h-full text-gray-500 hover:text-gray-900 transition-colors relative z-20"
                  :class="{ 'nav-active text-gray-900': route.path.startsWith('/compte') }"
                >
                    <!-- Check prefix logic to keep consistent with old code -->
                    <User class="h-6 w-6 transition-transform duration-300" :class="{ '-translate-y-0.5': route.path.startsWith('/compte') }" />
                    <span class="text-xs font-bold">Compte</span>
                </RouterLink>
              </template>
              <template v-else>
                <RouterLink 
                  to="/auth/connexion" 
                  class="flex-1 flex flex-col items-center justify-center gap-0.5 h-full text-gray-500 hover:text-gray-900 transition-colors relative z-20"
                  :class="{ 'nav-active text-gray-900': route.path.startsWith('/auth') }"
                >
                    <User class="h-6 w-6 transition-transform duration-300" :class="{ '-translate-y-0.5': route.path.startsWith('/auth') }" />
                    <span class="text-xs font-bold">Connexion</span>
                </RouterLink>
              </template>
          </div>

        </div>
      </div>

      <!-- Mobile Full Menu Overlay -->
      <transition 
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="showMobileMenu" class="fixed inset-0 z-[60] bg-white md:hidden overflow-y-auto">
           <div class="flex justify-between items-center p-6 border-b border-gray-100">
              <span class="font-baloo text-2xl font-bold text-gray-900">Menu</span>
              <button @click="closeMobileMenu" class="p-2 rounded-full bg-gray-50 hover:bg-gray-100">
                 <X class="w-6 h-6 text-gray-900" />
              </button>
           </div>
           
           <div class="p-6 space-y-8">
              <!-- Main Links -->
              <nav class="space-y-4">
                 <RouterLink to="/" @click="closeMobileMenu" class="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 text-lg font-bold text-gray-900">
                    <Home class="w-6 h-6" /> Accueil
                 </RouterLink>
                 <RouterLink to="/concept" @click="closeMobileMenu" class="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 text-lg font-bold text-gray-900">
                    <Armchair class="w-6 h-6" /> Offre Maison
                 </RouterLink>
                 <RouterLink to="/entreprise" @click="closeMobileMenu" class="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 text-lg font-bold text-gray-900">
                    <Building2 class="w-6 h-6" /> Offre Entreprise
                 </RouterLink>
              </nav>

              <!-- Secondary Links -->
              <div class="space-y-4 pt-4 border-t border-gray-100">
                 <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider">Informations</h3>
                 <RouterLink to="/livraison" @click="closeMobileMenu" class="block text-gray-600 font-medium">Livraison</RouterLink>
                 <RouterLink to="/faq" @click="closeMobileMenu" class="block text-gray-600 font-medium">FAQ</RouterLink>
                 <RouterLink to="/contact" @click="closeMobileMenu" class="block text-gray-600 font-medium">Contact</RouterLink>
              </div>

              <!-- Auth Actions (if not logged in) -->
              <div v-if="!authStore.isLoggedIn" class="pt-4 mt-auto">
                 <div class="grid grid-cols-2 gap-4">
                    <RouterLink to="/auth/connexion" @click="closeMobileMenu" class="py-4 rounded-2xl border-2 border-gray-200 text-center font-bold text-gray-900">
                       Connexion
                    </RouterLink>
                    <RouterLink to="/auth/inscription" @click="closeMobileMenu" class="py-4 rounded-2xl bg-[#ffd93e] text-center font-bold text-gray-900 shadow-lg shadow-[#ffd93e]/20">
                       Inscription
                    </RouterLink>
                 </div>
              </div>
              <div v-else class="pt-4 mt-auto">
                 <button @click="authStore.logout(); closeMobileMenu()" class="w-full py-4 rounded-2xl bg-red-50 text-red-600 font-bold flex items-center justify-center gap-2">
                    <LogOut class="w-5 h-5" /> Déconnexion
                 </button>
              </div>
           </div>
        </div>
      </transition>

      </template>

      <!-- FAB rapport admin (web + mobile web, jamais natif, jamais client) -->
      <ReportFab v-if="isUserAdmin && !isNative" @sent="onReportSent" />

      <!-- Admin Mobile Dock (persistent across admin pages) -->
      <AdminMobileDock v-if="isAdmin && showAdmin" />

    </template>

    <!-- Si Page Prototype, affichage brut sans layout -->
    <RouterView v-else />

    <!-- Cookie Consent : web uniquement. En natif, seuls des cookies
         essentiels sont utilises (exemptes de consentement), pas de banniere. -->
    <CookieConsent v-if="!isNative" />

  </div>
</template>

<style scoped>
</style>